import React from 'react';
import { ChevronDown } from 'lucide-react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Typography,
  Button,
  Stack,
  Chip
} from '@mui/material';
import { styled } from '@mui/material/styles';

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  width: '100%',
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'white',
  },
}));

const ClearButton = styled(Button)(({ theme }) => ({
  width: '100%',
  fontSize: '0.75rem',
  padding: '6px 12px',
  marginBottom: theme.spacing(1),
  justifyContent: 'center',
  textTransform: 'none',
  color: '#475569',
  '&:hover': {
    backgroundColor: '#f1f5f9',
  },
}));

const MultiSelect = ({ label, options, selected, onChange, testId }) => {
  const handleChange = (event) => {
    const value = event.target.value;
    onChange(typeof value === 'string' ? value.split(',') : value);
  };

  const handleClear = () => {
    onChange([]);
  };

  const renderValue = (selectedValues) => {
    if (!selectedValues || selectedValues.length === 0) {
      return (
        <Typography variant="body2" sx={{ color: '#64748b' }}>
          Select {label}
        </Typography>
      );
    }
    
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="body2" sx={{ fontWeight: 500, color: '#1e293b' }}>
          {selectedValues.length} selected
        </Typography>
      </Stack>
    );
  };

  return (
    <Box data-testid={testId}>
      <StyledFormControl fullWidth>
        <InputLabel 
          sx={{ 
            fontSize: '0.875rem',
            fontWeight: 500,
            '&.Mui-focused': {
              color: '#4f46e5',
            },
            
          }}
        >
          {label}
        </InputLabel>
        <Select
          multiple
          value={selected}
          onChange={handleChange}
          input={<OutlinedInput label={label} />}
          renderValue={renderValue}
          IconComponent={() => <ChevronDown size={18} style={{ marginRight: '12px', color: '#64748b' }} />}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: 256,
                width: 250,
              },
            },
          }}
          data-testid={`${testId}-trigger`}
          sx={{
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#cbd5e1',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#94a3b8',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#4f46e5',
              borderWidth: '2px',
            },
          }}
        >
          {/* Clear All Button */}
          {selected.length > 0 && (
            <Box sx={{ px: 1.5, pt: 1, pb: 0.5, borderBottom: '1px solid #e2e8f0' }}>
              <ClearButton
                size="small"
                onClick={handleClear}
                data-testid={`${testId}-clear`}
              >
                Clear All
              </ClearButton>
            </Box>
          )}
          
          {/* Options */}
          {options.map((option) => (
            <MenuItem 
              key={option} 
              value={option}
              data-testid={`${testId}-option-${option}`}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: '#eef2ff',
                },
                '&.Mui-selected:hover': {
                  backgroundColor: '#e0e7ff',
                },
              }}
            >
              <Checkbox 
                checked={selected.includes(option)} 
                size="small"
                sx={{
                  color: '#94a3b8',
                  '&.Mui-checked': {
                    color: '#4f46e5',
                  },
                  padding: '8px',
                  marginRight: '8px',
                }}
              />
              <ListItemText 
                primary={option}
                primaryTypographyProps={{
                  style: { fontSize: '0.875rem', color: '#1e293b' }
                }}
              />
            </MenuItem>
          ))}
        </Select>
      </StyledFormControl>
    </Box>
  );
};

export default MultiSelect;